import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  BookOpen,
  LibraryBig,
  Aperture,
  StickyNote,
  Settings,
  Shield,
  Highlighter,
  Compass,
  Send,
  MoreHorizontal,
  LogOut,
  HelpCircle,
  Sun,
  Moon,
  Monitor,
  Check,
  ExternalLink,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useTheme } from "@/hooks/useTheme";
import { composerCopy } from "@/lib/composer";

import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { FeedbackDialog } from "@/components/support/HelpWidget";
import type { Theme } from "@/lib/theme";

type BottomTabBarProps = {
  onOpenShortcuts: () => void;
};

type TabItem = {
  to: string;
  label: string;
  icon: typeof BookOpen;
  end?: boolean;
};

export const BottomTabBar = ({ onOpenShortcuts }: BottomTabBarProps) => {
  const { isAdmin } = useUserRole();
  const { active } = useWorkspace();
  const { signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // Readwise sync is ungated across all tiers (2026-05-10).
  // Composer is universal across tiers — see mem://features/composer-access-policy.md.

  // Super Admin Master Persona: scope down to platform-admin surfaces only.
  const isSuperAdminWorkspace = active?.account_tier === "super_admin";
  const tabs: TabItem[] = isSuperAdminWorkspace
    ? []
    : [
        { to: "/excerpts", label: "Excerpts", icon: BookOpen, end: true },
        { to: "/my-library", label: "Library", icon: LibraryBig },
        { to: "/notes", label: "Notes", icon: StickyNote },
        { to: "/readwise-highlights", label: "Readwise Highlights", icon: Highlighter },
      ];

  const totalCells = tabs.length + 1; // +1 for More

  const themeOptions: { value: Theme; icon: typeof Sun; label: string }[] = [
    { value: "light", icon: Sun, label: "Light" },
    { value: "system", icon: Monitor, label: "System" },
    { value: "dark", icon: Moon, label: "Dark" },
  ];

  const handleNavigate = (path: string) => {
    setMoreOpen(false);
    navigate(path);
  };

  return (
    <>
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 pb-[env(safe-area-inset-bottom)]"
        aria-label="Primary"
      >
        <ul className="grid" style={{ gridTemplateColumns: `repeat(${totalCells}, minmax(0, 1fr))` }}>
          {tabs.map(({ to, label, icon: Icon, end }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={Boolean(end)}
                aria-label={label}
                title={label}
                className={({ isActive }) =>
                  cn(
                    "relative flex items-center justify-center py-3 transition-colors",
                    isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span
                        aria-hidden
                        className="absolute top-0 left-2 right-2 h-0.5 rounded-full bg-primary"
                      />
                    )}
                    <Icon className="h-6 w-6" />
                  </>
                )}
              </NavLink>
            </li>
          ))}
          <li>
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              aria-label="More"
              title="More"
              className={cn(
                "relative flex w-full items-center justify-center py-3 transition-colors",
                moreOpen ? "text-primary" : "text-muted-foreground hover:text-foreground"
              )}
            >
              <MoreHorizontal className="h-6 w-6" />
            </button>
          </li>
        </ul>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="pb-[env(safe-area-inset-bottom)]">
          <SheetHeader className="text-left">
            <SheetTitle>More</SheetTitle>
          </SheetHeader>

          <div className="mt-4 space-y-1">
            <button
              type="button"
              onClick={() => handleNavigate("/composer")}
              className="flex w-full items-center gap-3 rounded-md px-3 py-3 text-sm font-medium hover:bg-accent transition-colors"
            >
              <Compass className="h-5 w-5" /> {composerCopy.navLabel}
            </button>
            <button
              type="button"
              onClick={() => handleNavigate("/publisher")}
              className="flex w-full items-center gap-3 rounded-md px-3 py-3 text-sm font-medium hover:bg-accent transition-colors"
            >
              <Send className="h-5 w-5" /> Publisher
            </button>
            <button
              type="button"
              onClick={() => handleNavigate("/image-studio")}
              className="flex w-full items-center gap-3 rounded-md px-3 py-3 text-sm font-medium hover:bg-accent transition-colors"
            >
              <Aperture className="h-5 w-5" /> Image Studio
            </button>
            <button
              type="button"
              onClick={() => handleNavigate("/settings")}
              className="flex w-full items-center gap-3 rounded-md px-3 py-3 text-sm font-medium hover:bg-accent transition-colors"
            >
              <Settings className="h-5 w-5" /> Settings
            </button>
            {isAdmin && (
              <button
                type="button"
                onClick={() => handleNavigate("/admin")}
                className="flex w-full items-center gap-3 rounded-md px-3 py-3 text-sm font-medium hover:bg-accent transition-colors"
              >
                <Shield className="h-5 w-5" /> Admin
              </button>
            )}
            <a
              href="/help"
              target="_blank"
              rel="noopener"
              onClick={() => setMoreOpen(false)}
              className="flex w-full items-center gap-3 rounded-md px-3 py-3 text-sm font-medium hover:bg-accent transition-colors"
            >
              <BookOpen className="h-5 w-5" />
              <span className="flex-1 text-left">Help center</span>
              <ExternalLink className="h-4 w-4 text-muted-foreground" aria-hidden />
            </a>
            <button
              type="button"
              onClick={() => {
                setMoreOpen(false);
                setFeedbackOpen(true);
              }}
              className="flex w-full items-center gap-3 rounded-md px-3 py-3 text-sm font-medium hover:bg-accent transition-colors"
            >
              <HelpCircle className="h-5 w-5" /> Send feedback
            </button>
            <button
              type="button"
              onClick={() => {
                setMoreOpen(false);
                signOut();
              }}
              className="flex w-full items-center gap-3 rounded-md px-3 py-3 text-sm font-medium hover:bg-accent transition-colors"
            >
              <LogOut className="h-5 w-5" /> Sign out
            </button>
          </div>

          <div className="mt-6">
            <p className="px-3 pb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Theme
            </p>
            <div className="flex gap-2 px-1">
              {themeOptions.map(({ value, icon: Icon, label }) => (
                <Button
                  key={value}
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "flex-1 h-11 gap-2",
                    theme === value && "bg-accent text-accent-foreground"
                  )}
                  onClick={() => setTheme(value)}
                  aria-pressed={theme === value}
                >
                  <Icon className="h-4 w-4" />
                  <span>{label}</span>
                  {theme === value && <Check className="h-4 w-4" />}
                </Button>
              ))}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </>
  );
};
